import useChartSort from '@/composables/chart-sort';
import useCustomBuckets from '@/composables/grid-page/chart/custom-buckets';
import useGoalCycles from '@/composables/goal-cycle/goal-cycle';
import useGoalProperty from '@/composables/property/goal-property';
import useLoggedInUser from '@/composables/logged-in-user/logged-in-user';
import usePropertyOptions from '@/composables/property-option/property-option';
import useSpaces from '@/composables/space/spaces';
import useStatusProperty from '@/composables/goal/status-property';
import useUsers from '@/composables/user/users';
import { UNASSIGNED_VALUE, cleanResult, edgeName, fillMissingCategories, getDayFromISOString, getDisplayValue, getMonthFromISOString, getQuarterFromISOString, getWeekFromISOString, getYearFromISOString, isDateGrouping, roundNumber } from '@/composables/grid-page/chart/utils';
import { aggregatorType, chartGroupByOption, gridPageChartColorScheme, gridPageChartType, propertyType } from 'shared/constants.json';
import { applyColorScheme, getColor, getSeriesColor } from '@/lib/charts/colors';
import { computed } from 'vue';
import { copy } from 'shared/lib/copy';
import { goal as goalConfig } from 'shared/api/query/configs.json';
import { textByLang } from 'shared/lib/language';
import { useI18n } from 'vue-i18n';

export default function useChartGrouping(chart, resultRaw) {
  const { t } = useI18n();
  const customBucket = computed(() => chart.value.groupBy.customBucket);
  const { findBucketLabel: findCustomBucketLabel } = useCustomBuckets(customBucket, resultRaw, computed(() => chart.value.groupBy));
  const stackedCustomBucket = computed(() => chart.value.aggregationConfig.stackOptions.customBucket);
  const { findBucketLabel: findStackedBucketLabel } = useCustomBuckets(stackedCustomBucket, resultRaw, computed(() => chart.value.aggregationConfig.stackOptions));
  const { userLang } = useLoggedInUser();
  const { selectSingle: selectGoalCycle, goalCycles } = useGoalCycles();
  const { selectSingle: selectPropertyOption } = usePropertyOptions();
  const { selectSingle: selectSpace, spaces } = useSpaces();
  const { selectSingle: selectUser, users } = useUsers();
  const { statusProperty } = useGoalProperty();
  const { leafOptions: leafStatuses } = useStatusProperty(statusProperty, userLang);

  const { sort } = useChartSort();

  const isStacked = computed(() => [gridPageChartType.line, gridPageChartType.bar, gridPageChartType.column, gridPageChartType.table].includes(chart.value.chartType)
      && chart.value.aggregationConfig.stackOptions.groupBy !== chartGroupByOption.none);

  const dateGroupingFunctions = {
    [chartGroupByOption.day]: getDayFromISOString,
    [chartGroupByOption.week]: getWeekFromISOString,
    [chartGroupByOption.month]: getMonthFromISOString,
    [chartGroupByOption.quarter]: getQuarterFromISOString,
    [chartGroupByOption.year]: getYearFromISOString,
  };

  const aggregationFn = {
    [aggregatorType.count]: (acc, item) => ({ count: acc.count + item.count }),
    [aggregatorType.sum]: (acc, item) => ({ sum: acc.sum + item.sum }),
    [aggregatorType.avg]: (acc, item) => {
      const total = (acc.avg * acc.count) + (item.avg * item.count);
      const count = acc.count + item.count;
      return { avg: total / count, count };
    },
    [aggregatorType.min]: (acc, item) => {
      if (acc.min === null || item.min < acc.min) {
        return { min: item.min };
      }
      return acc;
    },
    [aggregatorType.max]: (acc, item) => {
      if (acc.max === null || item.max > acc.max) {
        return { max: item.max };
      }
      return acc;
    },
    [aggregatorType.countDistinct]: (acc, item) => {
      if (acc.values[item.subGroup] === undefined) {
        acc[aggregatorType.countDistinct] += 1;
        acc.values[item.subGroup] = {};
      }
      return acc;
    },
  };

  const totalAggregator = {
    [aggregatorType.count]: aggregationFn[aggregatorType.count],
    [aggregatorType.sum]: aggregationFn[aggregatorType.sum],
    [aggregatorType.avg]: aggregationFn[aggregatorType.avg],
    [aggregatorType.min]: aggregationFn[aggregatorType.min],
    [aggregatorType.max]: aggregationFn[aggregatorType.max],
    [aggregatorType.countDistinct]: (acc, item) => {
      acc.values = { ...acc.values, ...item.values };
      acc[aggregatorType.countDistinct] = Object.keys(acc.values).length;
      return acc;
    },
  };

  const initialValue = {
    [aggregatorType.count]: { count: 0 },
    [aggregatorType.sum]: { sum: 0 },
    [aggregatorType.avg]: { avg: 0, count: 0 },
    [aggregatorType.min]: { min: Infinity },
    [aggregatorType.max]: { max: -Infinity },
    [aggregatorType.countDistinct]: { [aggregatorType.countDistinct]: 0, values: {} },
  };

  const getGroupEntity = (groupBy, groupValue) => {
    if (groupBy.groupBy === chartGroupByOption.none) {
      return undefined;
    }

    if (groupBy.isProperty === false && groupBy.edge === goalConfig.edges.goalCycle) {
      return selectGoalCycle(groupValue);
    }

    if (groupBy.isProperty === false) {
      return undefined;
    }

    if (groupBy.property?.type === propertyType.space) {
      return selectSpace(groupValue);
    }

    if (groupBy.property?.type === propertyType.user) {
      return selectUser(groupValue);
    }

    if (groupBy.property?.options) {
      return selectPropertyOption(groupValue);
    }

    return undefined;
  };

  const getGroups = (result, groupBy, valueKey, isStackOptions = false) => {
    const groupMap = new Map();
    result.forEach((item) => {
      const group = groupByMap(groupBy.groupBy, isStackOptions)(item[valueKey], userLang.value);
      if (!groupMap.has(group)) {
        groupMap.set(group, true);
      }
    });

    return Array.from(groupMap.keys()).map((g) => {
      const entity = getGroupEntity(groupBy, g);
      return {
        title: getDisplayValue(g, entity, groupBy, userLang.value),
        originalValue: g,
        color: getColor(entity?.color),
        ...(entity !== undefined && entity !== null && { hasEntityColor: true }),
      };
    });
  };

  const groupByMap = (groupBy, isStackOptions = false) => {
    if (isDateGrouping(groupBy)) {
      return dateGroupingFunctions[groupBy];
    }
    if (groupBy === chartGroupByOption.bucket) {
      if (isStackOptions) {
        return findStackedBucketLabel;
      }
      return findCustomBucketLabel;
    }
    return (val) => val;
  };

  const groupResult = (result, aggregation, group, subGroup = null) => result.reduce((acc, item) => {
    const groupKey = groupByMap(group.groupBy)(item[group.valueKey], userLang.value);
    if (!acc[groupKey]) {
      acc[groupKey] = { groups: {}, ...copy(initialValue[aggregation]) };
    }
    acc[groupKey] = { ...acc[groupKey], ...aggregationFn[aggregation](acc[groupKey], item) };
    if (subGroup === null) {
      return acc;
    }
    const subGroupKey = groupByMap(subGroup.groupBy, true)(item[subGroup.valueKey], userLang.value);
    if (!acc[groupKey].groups[subGroupKey]) {
      acc[groupKey].groups[subGroupKey] = copy(initialValue[aggregation]);
    }
    acc[groupKey].groups[subGroupKey] = { ...acc[groupKey].groups[subGroupKey], ...aggregationFn[aggregation](acc[groupKey].groups[subGroupKey], item) };
    return acc;
  }, {});

  const shouldShowUnassigned = (excludeNullValues, unassignedValue) => {
    if (unassignedValue === null) return false;
    if (Array.isArray(unassignedValue)) return !excludeNullValues || unassignedValue.length > 0;
    return !(excludeNullValues && unassignedValue === 0);
  };

  const unassignedCategory = (groupBy) => {
    let title = t('gridPageChartEditor.unassignedValue');
    if (groupBy) {
      const hasLabel = groupBy.isProperty
        ? groupBy.property?.label && textByLang(groupBy.property.label, userLang.value)
        : groupBy.edge;
      if (hasLabel) {
        const name = edgeName(groupBy, t, userLang.value);
        if (name) {
          title = t('gridPageChartEditor.unassignedEdge', { edge: name });
        }
      }
    }
    return { title, originalValue: UNASSIGNED_VALUE, color: getColor(null) };
  };

  const addToGroupedRes = (groupedRes, aggregator, categoryKey, stackKey, item) => {
    if (!groupedRes[categoryKey]) {
      groupedRes[categoryKey] = { groups: {}, ...copy(initialValue[aggregator]) };
    }
    if (!groupedRes[categoryKey].groups[stackKey]) {
      groupedRes[categoryKey].groups[stackKey] = copy(initialValue[aggregator]);
    }
    const stackEntry = groupedRes[categoryKey].groups[stackKey];
    groupedRes[categoryKey].groups[stackKey] = { ...stackEntry, ...aggregationFn[aggregator](stackEntry, item) };
    const categoryEntry = groupedRes[categoryKey];
    groupedRes[categoryKey] = { ...categoryEntry, ...aggregationFn[aggregator](categoryEntry, item) };
  };

  const processChartData = (result, unassignedValue = null, noStackData = []) => {
    const aggregator = chart.value.aggregationConfig.aggregation.aggregator;
    const excludeNullValues = chart.value.excludeNullValues ?? false;
    const fillContext = {
      goalCycles: goalCycles.value,
      spaces: spaces.value,
      users: users.value,
      leafStatuses: leafStatuses.value,
      getColor,
    };

    const filteredResult = cleanResult(
      result,
      chart.value.groupBy,
      chart.value.aggregationConfig.stackOptions,
      userLang.value,
      fillContext,
    );

    const showUnassigned = shouldShowUnassigned(excludeNullValues, unassignedValue);

    if (isStacked.value) {
      const stackedGroups = getGroups(filteredResult, chart.value.aggregationConfig.stackOptions, 'stackGroup', true);
      const groups = getGroups(filteredResult, chart.value.groupBy, 'val');
      const groupedRes = groupResult(
        filteredResult,
        aggregator,
        { groupBy: chart.value.groupBy.groupBy, valueKey: 'val' },
        { groupBy: chart.value.aggregationConfig.stackOptions.groupBy, valueKey: 'stackGroup' },
      );

      // Items with main groupBy attr but WITHOUT stack attr → "No value" stack per category
      let hasNoStackInStack = false;
      if (noStackData.length > 0) {
        const resolvedNoStack = cleanResult(
          noStackData,
          chart.value.groupBy,
          { groupBy: chartGroupByOption.none },
          userLang.value,
          fillContext,
        );
        resolvedNoStack.forEach((item) => {
          addToGroupedRes(groupedRes, aggregator, item.val, UNASSIGNED_VALUE, item);
        });
        hasNoStackInStack = true;
      }

      // Build unassigned entry with per-stack groups from raw @groupby entries
      if (showUnassigned && Array.isArray(unassignedValue)) {
        // Separate "no stack" entries (items missing both attrs) from regular stack entries
        const regularEntries = unassignedValue.filter((item) => item.stackGroup !== UNASSIGNED_VALUE);
        const noStackEntries = unassignedValue.filter((item) => item.stackGroup === UNASSIGNED_VALUE);

        // Resolve regular stack entries via cleanResult (handles hex UIDs, ___UID___, etc.)
        const remapped = regularEntries.map((item) => ({ ...item, val: item.stackGroup }));
        const resolvedUnassigned = cleanResult(
          remapped,
          chart.value.aggregationConfig.stackOptions,
          { groupBy: chartGroupByOption.none },
          userLang.value,
          fillContext,
        );
        resolvedUnassigned.forEach((item) => {
          addToGroupedRes(groupedRes, aggregator, UNASSIGNED_VALUE, item.val, item);
        });

        // Add "no stack" items to the UNASSIGNED_VALUE stack group
        noStackEntries.forEach((item) => {
          addToGroupedRes(groupedRes, aggregator, UNASSIGNED_VALUE, UNASSIGNED_VALUE, item);
        });

        if (noStackEntries.length > 0) {
          hasNoStackInStack = true;
        }
      }

      const aggregatedTotal = Object.values(groupedRes).reduce(
        totalAggregator[aggregator],
        copy(initialValue[aggregator]),
      );
      const sortedGroups = sort(groups, chart.value.groupBy);
      const categories = fillMissingCategories(sortedGroups, chart.value.groupBy, userLang.value, excludeNullValues, fillContext);
      if (showUnassigned) {
        categories.push(unassignedCategory(chart.value.groupBy));
      }
      const sortedStackedGroups = sort(stackedGroups, chart.value.aggregationConfig.stackOptions);
      const filledStackedGroups = fillMissingCategories(sortedStackedGroups, chart.value.aggregationConfig.stackOptions, userLang.value, excludeNullValues, fillContext);
      // Add "No value" stack group AFTER fillMissingCategories (which replaces the list for enumerable types)
      if (hasNoStackInStack && !filledStackedGroups.some((g) => g.originalValue === UNASSIGNED_VALUE)) {
        filledStackedGroups.push(unassignedCategory(chart.value.aggregationConfig.stackOptions));
      }

      let total = roundNumber(aggregatedTotal[aggregator]);
      if (showUnassigned && typeof unassignedValue === 'number') {
        total = roundNumber(total + unassignedValue);
      }

      const coloredStackedGroups = applyColorScheme(chart.value.colorScheme, filledStackedGroups, { colorUnassigned: true });
      return {
        categories: categories.map((category) => category.title),
        series: coloredStackedGroups.map((stackGroup) => ({
          name: stackGroup.title,
          color: stackGroup.color,
          data: categories.map((category) => {
            const value = groupedRes[category.originalValue]?.groups[stackGroup.originalValue]?.[aggregator];
            return { y: value !== undefined ? roundNumber(value) : 0, name: stackGroup.title };
          }),
        })),
        total,
      };
    }
    const groupedResult = groupResult(
      filteredResult,
      aggregator,
      { groupBy: chart.value.groupBy.groupBy, valueKey: 'val' },
    );
    const aggregatedTotal = Object.values(groupedResult).reduce(
      totalAggregator[aggregator],
      copy(initialValue[aggregator]),
    );
    let total = roundNumber(aggregatedTotal[aggregator]);
    if (showUnassigned) {
      total = roundNumber(total + unassignedValue);
    }

    const groups = getGroups(filteredResult, chart.value.groupBy, 'val');
    const sortedGroups = sort(groups, chart.value.groupBy);
    const categories = fillMissingCategories(sortedGroups, chart.value.groupBy, userLang.value, excludeNullValues, fillContext);
    if (showUnassigned) {
      categories.push(unassignedCategory(chart.value.groupBy));
    }

    const isLine = chart.value.chartType === gridPageChartType.line;
    const isPie = chart.value.chartType === gridPageChartType.pie;
    const scheme = chart.value.colorScheme;
    const isMonochromatic = scheme && scheme !== gridPageChartColorScheme.automatic && scheme !== gridPageChartColorScheme.colorful;
    const useSingleColor = !isLine && !isPie && isMonochromatic;
    const coloredCategories = applyColorScheme(scheme, categories);
    const singleColor = useSingleColor ? coloredCategories[0]?.color : null;
    const seriesData = coloredCategories.map((category) => {
      let y = 0;
      if (category.originalValue === UNASSIGNED_VALUE) {
        y = roundNumber(unassignedValue);
      } else if (groupedResult[category.originalValue]) {
        y = roundNumber(groupedResult[category.originalValue][aggregator]);
      }
      const color = useSingleColor ? singleColor : category.color;
      return { name: category.title, y, ...(!isLine && { color }) };
    });
    const series = [{ data: seriesData }];
    if (isLine) {
      series[0].color = getSeriesColor(scheme);
    }
    return {
      categories: coloredCategories.map((category) => category.title),
      series,
      total,
    };
  };

  return { processChartData };
}
